import Group from '../../utils/Group'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from '../../Common/Spacing/VerticalSpacer'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { OfferListSortOption } from 'constants/offerListFilters'
import React, { ComponentProps, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import OfferListFilterSidebar from 'components/OfferList/OfferListFilter/OfferListFilterSidebar'
import { connect } from 'react-redux'
import MobileOfferListFilters from 'components/OfferList/OfferListFilter/MobileOfferListFilters'
import noop from 'lib/function/noop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { searchSwitchViewClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import { pluralizeToString } from 'lib/string/pluralize'
import getOfferListKey from 'lib/offer/offerListKey'
import { EnabledFilters } from 'components/Search/type'
import HotelBroadSearchLocation from './HotelSearchBroadSearchLocation'
import useSlimMobileSearch from 'components/SearchV2/Hooks/useSlimMobileSearch'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import { DestinationLandingPageType } from '../DestinationLandingPage/DestinationLandingPageUtils'
import { selectLoggedIn } from '../../../selectors/accountSelectors'
import HotelSearchAddDestinationToggle from './HotelAddDestinationToggle/HotelSearchAddDestinationToggle'
import { useScreenSizeOnly } from '../../../hooks/useScreenSize'
import { ANYWHERE_PLACE_ID } from '../../../constants/search'
import * as Analytics from 'analytics/analytics'

const Container = styled(CSSBreakpoint)`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const MobileContainer = styled(CSSBreakpoint)`
  width: 100%;
`

const MobileCountTextWrapper = styled.div`
  padding-left: ${rem(12)};
`

const FilterAndCountContainer = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: ${rem(16)};
  }
`

const ButtonContainer = styled.div`
  display: flex;
  > * + * {
    margin-left: ${rem(8)};
  }
`

interface MappedStateProps {
  availableFilters?: App.OfferListFilterOptions
  isLoggedIn: boolean
  userId?: string
}

type InheritedOfferSortControlProps = Pick<ComponentProps<typeof OfferSortControl>, 'onSortChange'>
type InheritedOfferListFilterSidebarProps = Pick<ComponentProps<typeof OfferListFilterSidebar>, 'onFilterChange'>

interface Props extends InheritedOfferSortControlProps, InheritedOfferListFilterSidebarProps {
  mainListTotal: number
  sortOptions?: Array<OfferListSortOption>
  mapViewUrl?: string
  isFlexible?: boolean
  shouldShowFilters?: boolean
  filters: App.OfferListFilters
  windowSearch: string
  showOfferCount?: boolean
  showHolidayTypes?: boolean
  searchType?: string
  enabledFilters?: EnabledFilters
  fetching?: boolean
  countTextOverride?: string
  specificPlaces?: Array<App.SpecificPlace>
  destinationName?: string
  isDestinationLandingPage?: boolean
  urlLocationForDestinationLandingPage?: string
  pageTypeForDestinationLandingPage?: DestinationLandingPageType
  holidayTypeForDestinationLandingPage?: string
}

function OfferCountAndSort(props: Props & MappedStateProps) {
  const {
    mainListTotal,
    sortOptions,
    mapViewUrl,
    isFlexible,
    shouldShowFilters = false,
    showOfferCount = true,
    filters,
    windowSearch,
    searchType,
    enabledFilters,
    fetching,
    countTextOverride,
    specificPlaces,
    destinationName,
    isDestinationLandingPage,
    pageTypeForDestinationLandingPage,
    holidayTypeForDestinationLandingPage,
    isLoggedIn,
    userId,
    onFilterChange,
    onSortChange,
  } = props
  const propertySearch = searchType === 'property'
  const { isFilterPanelOpen, toggleFilterPanel } = useGlobalSearchURLHashFilter()

  const showSlimMobileSearch = useSlimMobileSearch()
  const isMobile = useScreenSizeOnly('mobile')

  const countText = (() => {
    if (countTextOverride) {
      return countTextOverride
    }
    if (propertySearch) {
      return ''
    }
    if (isMobile) {
      return pluralizeToString('offer', mainListTotal)
    }
    return isFlexible ? `Showing ${pluralizeToString('offer', mainListTotal)} with cheapest availabilities for your dates` : `Showing ${pluralizeToString('offer', mainListTotal)}`
  })()

  const isLuxuryEscapes = config.BRAND === 'luxuryescapes'
  const hasSortOptions = sortOptions && sortOptions?.length > 0

  const handleMapClick = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'search_map_view',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })
    addGTMEvent(searchSwitchViewClick())
  }, [])

  return (<>
    <div>
      {!showSlimMobileSearch && <MobileContainer max="mobile">
        <MobileOfferListFilters
          mapViewPath={mapViewUrl}
          onSortAndFilterClick={toggleFilterPanel}
          onViewSwitch={noop}
          hasSortOptions={hasSortOptions}
          showFilterIcon={false}
        />
      </MobileContainer>}
      <VerticalSpacer gap={16}>
        {!!specificPlaces?.length && !!destinationName &&
          <HotelBroadSearchLocation
            specificPlaces={specificPlaces}
            destinationName={destinationName}
            isDestinationLandingPage={isDestinationLandingPage}
            pageTypeForDestinationLandingPage={pageTypeForDestinationLandingPage}
            holidayTypeForDestinationLandingPage={holidayTypeForDestinationLandingPage}
          />}
        <MobileContainer max="mobile">
          <Group direction="horizontal" horizontalAlign="space-between">
            {showOfferCount && !fetching && (
              <MobileCountTextWrapper>
                <BodyText variant="medium">{countText}</BodyText>
              </MobileCountTextWrapper>
            )}
            {isLuxuryEscapes && isLoggedIn && filters.destinationId && filters.destinationId !== ANYWHERE_PLACE_ID && userId && <HotelSearchAddDestinationToggle userId={userId} placeId={filters.destinationId} />}
          </Group>
        </MobileContainer>
        <Container min="tablet">
          <FilterAndCountContainer>
            {shouldShowFilters && <AssistChip size="medium" variant="filled" startIcon={<LineFilterAltIcon />} onClick={toggleFilterPanel}>
              Filters
            </AssistChip>}
            {showOfferCount && !fetching && <BodyText variant="large">{countText}</BodyText>}
          </FilterAndCountContainer>
          <ButtonContainer>
            {hasSortOptions && <OfferSortControl sortOptions={sortOptions} onSortChange={onSortChange} />}
            {mapViewUrl &&
              <TextButton
                kind="secondary"
                variant="dark"
                to={mapViewUrl}
                startIcon={<LineMapIcon />}
                onClick={handleMapClick}
              >
                Map view
              </TextButton>}
          </ButtonContainer>
        </Container>
      </VerticalSpacer>
    </div>
    {!!enabledFilters && <OfferListFilterSidebar
      open={isFilterPanelOpen}
      onClose={toggleFilterPanel}
      filters={filters}
      sortOptions={sortOptions}
      search={windowSearch}
      enabledFilters={enabledFilters}
      onFilterChange={onFilterChange}
    />}
  </>)
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const offerListKey = getOfferListKey(ownProps.filters)

  return {
    availableFilters: appState.offer.offerListFilterOptions[offerListKey],
    isLoggedIn: selectLoggedIn(appState),
    userId: appState.auth.account.memberId,
  }
})(OfferCountAndSort)
